import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import AddIcon from '@atlaskit/icon/core/add';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Radio from '@atlaskit/radio/radio';
import { token } from '@atlaskit/tokens';
import Toggle from '@atlaskit/toggle/toggle';

import type { ApprovalMode, RecommendationType } from './recommendationType.ts';

type SettingCardProps = {
	type: RecommendationType;
	onToggle: (id: string) => void;
	onSetApproval: (id: string, mode: ApprovalMode) => void;
	onAddTrigger: (id: string) => void;
};

export function SettingCard({
	type,
	onToggle,
	onSetApproval,
	onAddTrigger,
}: SettingCardProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(false);

	const handleToggleExpand = useCallback(() => {
		setIsExpanded((prev) => !prev);
	}, []);

	const handleToggleEnabled = useCallback(() => {
		onToggle(type.id);
	}, [onToggle, type.id]);

	const handleSelectReview = useCallback(() => {
		onSetApproval(type.id, 'review');
	}, [onSetApproval, type.id]);

	const handleSelectAuto = useCallback(() => {
		onSetApproval(type.id, 'auto');
	}, [onSetApproval, type.id]);

	const handleAddTrigger = useCallback(() => {
		onAddTrigger(type.id);
	}, [onAddTrigger, type.id]);

	const expandLabel = formatMessage(isExpanded ? i18n.collapseCardLabel : i18n.expandCardLabel, {
		title: type.title,
	});

	const approvalGroupName = `approval-${type.id}`;

	return (
		<Box xcss={styles.card} testId={`setting-card-${type.id}`}>
			<Inline alignBlock="start" spread="space-between" space="space.200">
				<Stack space="space.050">
					<Text weight="bold">{type.title}</Text>
					<Text size="small" color="color.text.subtle">
						{type.description}
					</Text>
				</Stack>
				<Inline alignBlock="center" space="space.100">
					<Toggle
						isChecked={type.enabled}
						onChange={handleToggleEnabled}
						label={formatMessage(i18n.toggleLabel, { title: type.title })}
					/>
					<IconButton
						appearance="subtle"
						icon={isExpanded ? ChevronDownIcon : ChevronRightIcon}
						label={expandLabel}
						onClick={handleToggleExpand}
						isTooltipDisabled={false}
					/>
				</Inline>
			</Inline>

			{isExpanded && (
				<Stack xcss={styles.expandedBody} space="space.100">
					{/* Triggers section */}
					<Box xcss={styles.subSection}>
						<Stack space="space.050">
							<Inline alignBlock="center" spread="space-between" space="space.100">
								<Text weight="bold">{formatMessage(i18n.triggersLabel)}</Text>
								<IconButton
									appearance="subtle"
									icon={AddIcon}
									label={formatMessage(i18n.addTriggerLabel)}
									onClick={handleAddTrigger}
									isTooltipDisabled={false}
									testId={`setting-card-${type.id}-add-trigger`}
								/>
							</Inline>
							{type.triggers.length === 0 ? (
								<Text size="small" color="color.text.subtle">
									{formatMessage(i18n.noTriggers)}
								</Text>
							) : (
								<Stack space="space.050">
									{type.triggers.map((trigger, index) => (
										// TODO(DISCO): use a stable id once triggers are real objects from the API.
										// eslint-disable-next-line react/no-array-index-key
										<Text key={index} size="small">
											{trigger}
										</Text>
									))}
								</Stack>
							)}
						</Stack>
					</Box>

					{/* Approval section */}
					<Box xcss={styles.subSection}>
						<Stack space="space.100">
							<Text weight="bold">{formatMessage(i18n.approvalLabel)}</Text>
							<Stack space="space.100">
								<Stack space="space.025">
									<Radio
										name={approvalGroupName}
										value="review"
										isChecked={type.approval === 'review'}
										onChange={handleSelectReview}
										label={formatMessage(i18n.approvalReviewLabel)}
									/>
									<Box xcss={styles.radioHelper}>
										<Text size="small" color="color.text.subtlest">
											{formatMessage(i18n.approvalReviewDescription)}
										</Text>
									</Box>
								</Stack>
								<Stack space="space.025">
									<Radio
										name={approvalGroupName}
										value="auto"
										isChecked={type.approval === 'auto'}
										onChange={handleSelectAuto}
										label={formatMessage(i18n.approvalAutoLabel)}
									/>
									<Box xcss={styles.radioHelper}>
										<Text size="small" color="color.text.subtlest">
											{formatMessage(i18n.approvalAutoDescription)}
										</Text>
									</Box>
								</Stack>
							</Stack>
						</Stack>
					</Box>
				</Stack>
			)}
		</Box>
	);
}

const styles = cssMap({
	card: {
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		borderRadius: token('radius.xlarge'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('elevation.surface'),
	},
	expandedBody: {
		marginBlockStart: token('space.200'),
	},
	/**
	 * Triggers and Approval sub-sections inside an expanded card. Use a
	 * sunken surface with an xl radius to visually group the controls and
	 * separate them from the surrounding card surface.
	 */
	subSection: {
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface.sunken'),
	},
	/**
	 * The Radio component already renders its own label. The helper text below
	 * needs to be indented to line up with the radio label rather than the
	 * radio circle itself.
	 */
	radioHelper: {
		paddingInlineStart: token('space.300'),
	},
});

const i18n = defineMessages({
	toggleLabel: {
		id: 'space-suggestions.setting-card.toggle-label',
		defaultMessage: 'Enable {title}',
		description:
			'Accessible label for the toggle that enables/disables a single recommendation type. {title} is the recommendation type name.',
	},
	expandCardLabel: {
		id: 'space-suggestions.setting-card.expand-label',
		defaultMessage: 'Expand {title} settings',
		description:
			'Accessible label and tooltip for the icon button that expands the settings panel for a recommendation type. {title} is the recommendation type name.',
	},
	collapseCardLabel: {
		id: 'space-suggestions.setting-card.collapse-label',
		defaultMessage: 'Collapse {title} settings',
		description:
			'Accessible label and tooltip for the icon button that collapses the settings panel for a recommendation type. {title} is the recommendation type name.',
	},
	triggersLabel: {
		id: 'space-suggestions.setting-card.triggers-label',
		defaultMessage: 'Triggers',
		description: 'Section header for the list of triggers that fire a recommendation type.',
	},
	addTriggerLabel: {
		id: 'space-suggestions.setting-card.add-trigger-label',
		defaultMessage: 'Add trigger',
		description:
			'Accessible label and tooltip for the + icon button used to add a new trigger to a recommendation type.',
	},
	noTriggers: {
		id: 'space-suggestions.setting-card.no-triggers',
		defaultMessage: 'No triggers configured',
		description: 'Shown in the Triggers section when the recommendation type has no triggers yet.',
	},
	approvalLabel: {
		id: 'space-suggestions.setting-card.approval-label',
		defaultMessage: 'Approval',
		description: 'Section header for the approval-mode radio group on a recommendation type card.',
	},
	approvalReviewLabel: {
		id: 'space-suggestions.setting-card.approval-review-label',
		defaultMessage: 'Require review',
		description:
			'Radio label for the approval mode where the user must review each recommendation before it is applied.',
	},
	approvalReviewDescription: {
		id: 'space-suggestions.setting-card.approval-review-description',
		defaultMessage: 'Require approval before updating content',
		description: 'Helper text under the "Require review" radio explaining what selecting it does.',
	},
	approvalAutoLabel: {
		id: 'space-suggestions.setting-card.approval-auto-label',
		defaultMessage: 'Automatic',
		description:
			'Radio label for the approval mode where recommendations are applied automatically without review.',
	},
	approvalAutoDescription: {
		id: 'space-suggestions.setting-card.approval-auto-description',
		defaultMessage: 'Make updates to content automatically',
		description: 'Helper text under the "Automatic" radio explaining what selecting it does.',
	},
});
